/**
 * AuthContext - Authentication state management
 * Provides user authentication state and methods throughout the app
 *
 * Subscribes to the Amplify Hub so sign-in / sign-out / token-refresh events
 * coming from the Authenticator component flow through here without the user
 * having to reload the page (2026-06-25 audit).
 */

import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getCurrentUser, signOut, fetchAuthSession } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { attemptReferralClaim } from '../utils/referral.js';
import { attemptAttributionClaim } from '../utils/attribution.js';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check authentication status. forceRefresh asks Amplify to re-mint the
  // id token (used by Hub's tokenRefresh event below).
  const checkAuth = useCallback(async ({ forceRefresh = false } = {}) => {
    try {
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession(forceRefresh ? { forceRefresh: true } : undefined);
      setUser({
        ...currentUser,
        email: session.tokens?.idToken?.payload?.email,
      });
      setIsAuthenticated(true);
    } catch {
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Hub subscription — keeps AuthContext in sync with the Authenticator
  // render-prop without a page reload. signedIn fires after the user
  // completes a sign-in flow; tokenRefresh fires before token expiry;
  // signedOut covers Authenticator-driven sign-outs.
  useEffect(() => {
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signedIn':
          checkAuth();
          // Fallback referral attribution — no-op unless a ref is stored and
          // the account is new + unattributed. Fire-and-forget.
          attemptReferralClaim();
          // Same fallback for first-touch acquisition data (admin Traffic page).
          attemptAttributionClaim();
          break;
        case 'signedOut':
          setUser(null);
          setIsAuthenticated(false);
          break;
        case 'tokenRefresh':
          checkAuth({ forceRefresh: true });
          break;
        default:
          break;
      }
    });
    return unsubscribe;
  }, [checkAuth]);

  const handleSignOut = async () => {
    try {
      await signOut();
      setUser(null);
      setIsAuthenticated(false);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    signOut: handleSignOut,
    refreshAuth: checkAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
